import React, { useState } from "react";
import axios from "axios";
import { useLanguage } from "../utils/LanguageContext";
import { useTheme } from "../utils/ThemeContext";
import "../App.css";
import { toast } from "react-toastify";

const Contact = () => {
  const { language, t } = useLanguage();
  const { theme } = useTheme();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetData = () =>
    setFormData({ firstName: "", lastName: "", email: "", subject: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    const toastId = toast.loading(
      language === "en" ? "Sending message…" : "Envoi du message…"
    );
    const formspreeId = process.env.REACT_APP_FORMSPREE_ID;

    try {
      const res = await axios.post(
        `https://formspree.io/f/${formspreeId}`,
        formData,
        { headers: { "Content-Type": "application/json" } }
      );
      if (res.status === 200) {
        toast.update(toastId, {
          render: language === "en" ? "Message sent successfully!" : "Message envoyé avec succès !",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
        resetData();
      }
    } catch {
      toast.update(toastId, {
        render: language === "en"
          ? "Failed to send. Please try again."
          : "Échec de l'envoi. Veuillez réessayer.",
        type: "error",
        isLoading: false,
        autoClose: 4000,
        pauseOnHover: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const inputClass =
    "input-animated w-full text-base px-4 py-3 rounded-xl bg-slate-800 dark:bg-slate-50 border border-slate-700 dark:border-slate-200 text-white dark:text-slate-800 placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:border-violet-500 dark:focus:border-violet-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <section className="w-full" id="contact">
      <div className="flex flex-row justify-center items-start gap-x-16 w-full xxxs:flex-col xxs:flex-col xs:flex-col xxxs:gap-y-10 xxs:gap-y-10 xs:gap-y-10 py-12 dark:py-8">

        {/* Info column */}
        <div className="flex flex-col gap-4 w-[55%] xxxs:w-full xxs:w-full xs:w-full reveal reveal-left">
          <h2 className={`${theme === "dark" ? "title2-dark" : "title2"}`}>
            {t.contact.title}
          </h2>
          <h3 className="text-base font-semibold text-white dark:text-slate-800">
            {t.contact.subTitle}
          </h3>
          <p className="text-sm text-gray-400 dark:text-slate-600 leading-relaxed max-w-sm">
            {t.contact.info}
          </p>
          <ul className="mt-3 flex flex-col gap-y-3">
            {t.contact.list.map(({ icon, title, id, href }) => (
              <li
                key={id}
                className="skill-item flex flex-row items-center gap-x-3 text-gray-400 dark:text-slate-600 text-sm"
              >
                <span className="text-violet-400 dark:text-violet-500 flex-shrink-0">{icon}</span>
                {href ? (
                  <a href={href} className="hover:text-violet-400 dark:hover:text-violet-600 transition-colors duration-200">
                    {title}
                  </a>
                ) : (
                  <span>{title}</span>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Form card */}
        <div className="w-[45%] xxxs:w-full xxs:w-full xs:w-full reveal reveal-right">
          <div className="bg-slate-800/50 dark:bg-white dark:shadow-xl dark:border dark:border-slate-200 rounded-2xl p-6 sm:p-8">
            <form
              className="flex flex-col gap-4"
              onSubmit={handleSubmit}
              noValidate
            >
              <div className="grid grid-cols-2 gap-4 xxxs:grid-cols-1 xxs:grid-cols-1">
                <input
                  className={inputClass}
                  type="text"
                  placeholder={language === "en" ? "First name" : "Prénom"}
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
                <input
                  className={inputClass}
                  type="text"
                  placeholder={language === "en" ? "Last name" : "Nom"}
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <input
                className={inputClass}
                type="email"
                placeholder="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
              <textarea
                className={`${inputClass} resize-none`}
                placeholder="Message"
                rows={5}
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                autoComplete="off"
                disabled={isSubmitting}
              />
              <button
                className="btn-press w-fit flex items-center gap-2 bg-gradient-to-r from-violet-500 to-indigo-600 text-white px-7 py-2.5 rounded-xl font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 focus:ring-offset-slate-800 dark:focus:ring-offset-white disabled:opacity-60 disabled:cursor-not-allowed"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting && (
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                {language === "en"
                  ? isSubmitting ? "Sending…" : "Send Message"
                  : isSubmitting ? "Envoi…" : "Envoyer"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
